import { Form, Select } from "antd";
import { useSearchParams, useSubmit } from "react-router-dom";
import { useRouteActivity } from "~/hooks/useRouteActivity";
import { removeEmptyFields } from "~/utils";
import * as APITypes from "~/API";
import { TransactionListFiltersType } from "~/types";
import { PaymentStatusTag } from "./PaymentStatusTag";
import { useAppTranslation } from "~/hooks/useAppTranslation";
import {
  ResponsiveFilterChip,
  ResponsiveFilterGrid,
  ResponsiveFilterPanel,
} from "~/components/Filters";
import { useMemo } from "react";

export const PaymentTablePageFilters = ({
  filters,
}: {
  filters: TransactionListFiltersType;
}) => {
  const { t } = useAppTranslation();
  const [formInstance] = Form.useForm();
  const [searchParams] = useSearchParams();
  const submit = useSubmit();
  const { isTableLoading: isLoading } = useRouteActivity();

  const initialValues = useMemo(
    () => ({
      status:
        filters.status ??
        (searchParams.get("status") as APITypes.PaymentStatusType | undefined),
    }),
    [filters.status, searchParams],
  );

  if (!isLoading) {
    formInstance.setFieldsValue(initialValues);
  }

  const handleSubmit = () => {
    const formValues = removeEmptyFields(formInstance.getFieldsValue(true));
    submit(formValues, { method: "get" });
  };

  const handleClear = () => {
    formInstance.resetFields();
    submit({}, { method: "get" });
  };

  const activeFilters: ResponsiveFilterChip[] = [];
  if (initialValues.status) {
    activeFilters.push({
      key: "status",
      label: t("Status"),
      value: <PaymentStatusTag status={initialValues.status} />,
    });
  }

  return (
    <ResponsiveFilterPanel
      title={t("Filters")}
      activeFilters={activeFilters}
      isLoading={isLoading}
      onApply={handleSubmit}
      onClear={handleClear}
      closeDrawerOnClear
    >
      <Form form={formInstance} layout="vertical" initialValues={initialValues}>
        <ResponsiveFilterGrid>
          <Form.Item label={t("Status")} name="status">
            <Select
              placeholder={t("Select_a_status")}
              allowClear
              onChange={handleSubmit}
              disabled={isLoading}
            >
              {(
                [
                  APITypes.PaymentStatusType.Pending,
                  APITypes.PaymentStatusType.Processing,
                  APITypes.PaymentStatusType.Complete,
                  APITypes.PaymentStatusType.Failed,
                ] as APITypes.PaymentStatusType[]
              ).map((status) => (
                <Select.Option key={status} value={status}>
                  <PaymentStatusTag status={status} />
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </ResponsiveFilterGrid>
      </Form>
    </ResponsiveFilterPanel>
  );
};
